import { useData } from "@hooks/useData";
import type { components } from "@apiSchema";
import http from "@core/fetch";
import apiConfig from "@apiConfig";
import { RestaurantSegmentEnum } from "@enums";
import { iPermissionFields, PermissionSection } from "@models/PermissionSection";

type SegmentGrowthData = components["schemas"]["ChainsGrowthSegment"];
export type ChainsGrowthData = iPermissionFields & {
    data: {
        year?: number;
        segments: Map<RestaurantSegmentEnum, Partial<SegmentGrowthData>>;
    }
};

type Response = components["schemas"]["ChainsGrowthModelChainPermissionWrapper"];

const SEGMENT_NAME_TO_ENUM: Record<string, RestaurantSegmentEnum> = {
    'Casual Dining': RestaurantSegmentEnum.CASUAL_DINING,
    'Fast Casual': RestaurantSegmentEnum.FAST_CASUAL,
    'Fine Dining': RestaurantSegmentEnum.FINE_DINING,
    'Midscale Dining': RestaurantSegmentEnum.MIDSCALE_DINING,
    'Quick Service': RestaurantSegmentEnum.QUICK_SERVICE,
};

const REQUEST_TYPE: keyof typeof apiConfig = 'get home chains growth';
const DEFAULT_DATA: ChainsGrowthData = {
    permissionLevel: PermissionSection.getPermissionLevel(),
    hasData: true,
    data: {
        segments: new Map<RestaurantSegmentEnum, Partial<SegmentGrowthData>>()
    },
}
const transformResponse = (response: Response): ChainsGrowthData => {
    const { permissionLevel, hasData = true, data } = response;
    const segments = new Map<RestaurantSegmentEnum, Partial<SegmentGrowthData>>();
    const allSegmentsAverages: NonNullable<SegmentGrowthData["topChains"]> = [];

    data?.segments?.forEach((segmentData) => {
        const { segmentName, averageGrowth } = segmentData;
        const segmentId = segmentName ? SEGMENT_NAME_TO_ENUM[segmentName] : null;
        
        if (!segmentId) return;

        segments.set(segmentId, segmentData);

        if (averageGrowth && segmentName) {
            allSegmentsAverages.push({ ...averageGrowth, chainName: segmentName });
        }
    });

    segments.set(RestaurantSegmentEnum.ALL, { topChains: allSegmentsAverages });
        
    return {
        permissionLevel: PermissionSection.getPermissionLevel(permissionLevel),
        hasData,
        data: {
            year: data?.year,
            segments,
        },
    };
};

async function getHomeChainsGrowthData(): Promise<ChainsGrowthData> {
    try {
        const response: Response = await http.get(REQUEST_TYPE);
        return transformResponse(response);
    } catch (error) {
        console.error('Fetch error:', REQUEST_TYPE, error);
        return DEFAULT_DATA;
    }
}

export function useHomeChainsGrowthData() {
    return useData(DEFAULT_DATA, getHomeChainsGrowthData);
}